// @flow

// Effect Manager
import {
  fork,
  put,
  take,
  call,
  all,
} from 'redux-saga/effects'

// Action Types
import {
  LOAD_LAB_RESULTS,
  LOAD_MAP_REPORTS,
  SET_REPORTS_ERRORS,
  SET_REPORTS,
  START_SPINNER,
  STOP_SPINNER,
  DOWNLOAD_ZIP,
} from '../constants'

// Utils
import { getToken } from 'fc-auth'
import getUrl from 'hfty-utils/url'
import downloadBlobFile from 'hfty-utils/downloadBlobFile'

export default function *watchOperations (): Generator<*, void, *> {
  while (true) {
    const {payload, type} = yield take([
      LOAD_LAB_RESULTS,
      LOAD_MAP_REPORTS,
      DOWNLOAD_ZIP,
    ])
    if (type === LOAD_LAB_RESULTS && payload) {
      yield fork(handleLoadLabReports, payload)
    } else if (type === LOAD_MAP_REPORTS) {
      yield fork(handleLoadMapReports, payload)
    } else if (type === DOWNLOAD_ZIP) {
      yield fork(handleDownloadZip, payload)
    }
  }
}


/*
* Reusable sagas for making requests and handling errors regarding reports
* */
function *_sagaLoadData (url: string | number, options?: Object = {}): Generator<*, void, *> {
  const token = yield call(getToken)
  const URL = getUrl({
    path: url,
    ...options,
  })

  const response = yield call(fetch, URL, {
    method: 'GET',
    headers: {
      Authorization: `Token ${token}`,
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
    },
  })

  return yield response.json()
}

function *_sagaPutErrors (message: string): Generator<*, void, *> {
  yield put({ type: STOP_SPINNER })
  yield put({ type: SET_REPORTS_ERRORS, payload: message })
}


export function *handleLoadLabReports (assetId: number): Generator<*, void, *> {
  yield put({ type: START_SPINNER })

  try {
    const reports = yield call(_sagaLoadData, `report/?rootAsset=${assetId}`)

    if (Array.isArray(reports) && reports.length !== 0) {
      yield put({ type: SET_REPORTS, payload: reports })
      yield put({ type: STOP_SPINNER })
    } else {
      yield call(_sagaPutErrors, 'Error while loading lab reports')
    }
  } catch (e) {
    console.error('Error while loading labs reports', e)
  }
}

export function *handleLoadMapReports (recommendationId: number): Generator<*, void, *> {
  yield put({ type: START_SPINNER })

  const options = {
    excludeTrailingSlash: true,
  }

  try {
    const { reports } = yield call(_sagaLoadData, `hefty/recommendation/${recommendationId}`, options)

    if (reports.length > 0) {
      yield call(getMapReports, reports)
    } else {
      yield call(_sagaPutErrors, 'No reports found')
    }
  } catch (e) {
    console.error('Error while loading map reports', e)
  }
}

export function *getMapReports (reportsIds: Array): Generator<*, void, *> {
  let REPORTS = []

  function *_loadMapReport (id: number): Generator<*, void, *> {
    const token = yield call(getToken)

    const URL = getUrl({
      path: `report/${id}`,
      excludeTrailingSlash: true,
    })

    const response = yield call(fetch, URL, {
      method: 'GET',
      headers: {
        Authorization: `Token ${token}`,
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
    })

    return yield response.json()
  }

  try {
    /*
    * There could be a several reports, so make request for each
    * */
    const reports = yield all([
      reportsIds.map(_loadMapReport),
    ])

    for (let report of reports) {
      REPORTS = [
        ...REPORTS,
        ...report,
      ]
    }

    yield put({ type: SET_REPORTS, payload: REPORTS })
    yield put({ type: STOP_SPINNER })
  } catch (e) {
    console.error('Error while loading recommendation reports', e)
  }
}

/*
 * General saga for downloading .zip files (soil results, grid points, field boundary)
 * */
export function *handleDownloadZip (
  params: Object
): Generator<*, void, *> {
  const token = yield call(getToken)
  const URL = getUrl({
    path: params.path,
    queryParams: params.queryParams,
  })
  try {
    const response = yield call(fetch, URL, {
      method: 'GET',
      headers: {
        Authorization: `Token ${token}`,
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
    })

    if (response.status >= 200 && response.status < 300) {
      const blob = yield call([response, response.blob])
      yield call(downloadBlobFile, blob, params.fileName)
    } else {
      console.warn(`There was an error downloading ${params.errorItem} files.`)
    }
  } catch (e) {
    console.warn(`There was an error downloading ${params.errorItem} files.`)
  }
}
